import { useState } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  WorkflowInstanceSchema,
  type ApprovalStepView,
  type WorkflowInstanceDto,
} from '@taqon/contracts';
import { z } from 'zod';
import { getData, postJson } from '../api/client';
import { hasCapability, useSession } from '../session/session';
import { ConfirmDialog } from '../ui/confirm';
import { PersonName } from '../ui/person';
import { EmptyState, ErrorState, LoadingState, Section } from '../ui/page';

const STATE_TONE: Readonly<Record<string, 'default' | 'success' | 'warning' | 'error' | 'info'>> = {
  pending: 'info',
  approved: 'success',
  rejected: 'error',
  returned: 'warning',
  skipped: 'default',
  superseded: 'default',
  in_progress: 'info',
  withdrawn: 'default',
};

export function ApprovalStateChip({ state }: { state: string }) {
  return (
    <Chip
      size="small"
      variant="outlined"
      color={STATE_TONE[state] ?? 'default'}
      label={state.replace(/_/g, ' ')}
    />
  );
}

export const workflowHistoryKey = (recordType: string, recordId: string) => [
  'workflow',
  'history',
  recordType,
  recordId,
];

/**
 * Whether the signed-in user submitted the record's running approval, so may withdraw it (FRD §17
 * CLR-64). Reads the same history as the approval panel, so the two share one request. UX only:
 * the API refuses anyone else.
 */
export function useSubmittedByMe(recordType: string, recordId: string, inApproval: boolean) {
  const session = useSession();
  const history = useQuery({
    queryKey: workflowHistoryKey(recordType, recordId),
    enabled: inApproval && hasCapability(session.data, 'platform.approval_step:view'),
    queryFn: () =>
      getData(
        `/api/v1/workflow-instances?subjectRecordType=${recordType}&subjectRecordId=${recordId}`,
        z.array(WorkflowInstanceSchema),
      ),
  });
  return (
    inApproval &&
    session.data !== undefined &&
    session.data !== null &&
    (history.data ?? []).some(
      (i) => i.status === 'in_progress' && i.initiatedBy === session.data?.userId,
    )
  );
}

/**
 * A record's approvals (FRD-CORE-015, CORE-030): every submission, each step with who could
 * decide it, who did, the decision, when and the comment. An approver sees Approve / Reject /
 * Return on the step they may decide; the API re-checks permission, eligibility and SoD.
 */
export function ApprovalPanel({
  recordType,
  recordId,
  onDecided,
}: {
  recordType: string;
  recordId: string;
  onDecided?: () => void;
}) {
  const session = useSession();
  const canRead = hasCapability(session.data, 'platform.approval_step:view');
  const history = useQuery({
    queryKey: workflowHistoryKey(recordType, recordId),
    enabled: canRead,
    queryFn: () =>
      getData(
        `/api/v1/workflow-instances?subjectRecordType=${recordType}&subjectRecordId=${recordId}`,
        z.array(WorkflowInstanceSchema),
      ),
  });
  if (!canRead) return null;
  return (
    <Section title="Approvals">
      {history.isPending && <LoadingState label="Loading approvals" />}
      {history.isError && <ErrorState message={history.error.message} />}
      {history.data?.length === 0 && <EmptyState title="Not submitted for approval yet" />}
      {history.data !== undefined && history.data.length > 0 && (
        <Stack spacing={2} sx={{ p: 2 }}>
          {history.data.map((instance) => (
            <InstanceBlock
              key={instance.id}
              instance={instance}
              recordType={recordType}
              recordId={recordId}
              {...(onDecided !== undefined ? { onDecided } : {})}
            />
          ))}
        </Stack>
      )}
    </Section>
  );
}

function InstanceBlock({
  instance,
  recordType,
  recordId,
  onDecided,
}: {
  instance: WorkflowInstanceDto;
  recordType: string;
  recordId: string;
  onDecided?: () => void;
}) {
  const [deciding, setDeciding] = useState<{ step: ApprovalStepView; decision: Decision } | null>(
    null,
  );
  return (
    <Stack spacing={1}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }} useFlexGap>
        <ApprovalStateChip state={instance.status} />
        <Typography variant="body2">
          Submitted by{' '}
          <PersonName name={instance.initiatedByName} userId={instance.initiatedBy} size={20} /> on{' '}
          {new Date(instance.initiatedAt).toLocaleString()}
          {instance.completedAt !== null &&
            ` · completed ${new Date(instance.completedAt).toLocaleString()}`}
          {instance.resubmissionOfId !== null && ' · resubmission'}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Route: {instance.definitionName} (version {instance.definitionVersion})
        </Typography>
      </Stack>
      <Table size="small" aria-label="Approval steps">
        <TableHead>
          <TableRow>
            <TableCell>Step</TableCell>
            <TableCell>Approvers</TableCell>
            <TableCell>Decision</TableCell>
            <TableCell>By / when</TableCell>
            <TableCell>Comment</TableCell>
            <TableCell align="right" />
          </TableRow>
        </TableHead>
        <TableBody>
          {instance.steps.map((step) => (
            <TableRow key={step.id}>
              <TableCell sx={{ minWidth: 160 }}>
                {step.sequence}. {step.name}
                {step.escalatedAt !== null && (
                  <Chip
                    size="small"
                    label={
                      step.escalationFoundNobody
                        ? 'escalation found nobody — an administrator has been told'
                        : step.escalatedTo.length > 0
                          ? `escalated to ${step.escalatedTo.join(', ')}`
                          : 'escalated'
                    }
                    color="warning"
                    sx={{ ml: 1 }}
                  />
                )}
              </TableCell>
              <TableCell sx={{ minWidth: 160 }}>
                {step.unassignable ? (
                  <Typography variant="body2" color="warning.main">
                    No eligible approver — an administrator has been told
                  </Typography>
                ) : step.assignees.length === 0 ? (
                  '—'
                ) : (
                  <Stack spacing={0.5}>
                    {step.assignees.map((a) => (
                      <PersonName key={a.userId} name={a.name} userId={a.userId}>
                        {a.delegateOf === null ? a.name : `${a.name} (for ${a.delegateOf})`}
                      </PersonName>
                    ))}
                  </Stack>
                )}
              </TableCell>
              <TableCell>
                <ApprovalStateChip state={step.state} />
              </TableCell>
              <TableCell sx={{ minWidth: 140 }}>
                {step.actingUserName === null ? (
                  '—'
                ) : (
                  <PersonName name={step.actingUserName} userId={step.actingUserId} />
                )}
                {step.actingForName !== null && ` (for ${step.actingForName})`}
                {step.decidedAt !== null && (
                  <Typography variant="caption" component="span" sx={{ display: 'block' }}>
                    {new Date(step.decidedAt).toLocaleString()}
                  </Typography>
                )}
              </TableCell>
              <TableCell sx={{ overflowWrap: 'anywhere' }}>{step.comment ?? '—'}</TableCell>
              <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                {step.canDecide && (
                  <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end' }}>
                    <Button
                      size="small"
                      variant="contained"
                      color="success"
                      onClick={() => {
                        setDeciding({ step, decision: 'approve' });
                      }}
                    >
                      Approve
                    </Button>
                    <Button
                      size="small"
                      color="warning"
                      onClick={() => {
                        setDeciding({ step, decision: 'return' });
                      }}
                    >
                      Return
                    </Button>
                    <Button
                      size="small"
                      color="error"
                      onClick={() => {
                        setDeciding({ step, decision: 'reject' });
                      }}
                    >
                      Reject
                    </Button>
                  </Stack>
                )}
                {!step.canDecide &&
                  step.cannotDecideReason !== undefined &&
                  step.cannotDecideReason !== null && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', whiteSpace: 'normal', maxWidth: 280, ml: 'auto' }}
                    >
                      {step.cannotDecideReason}
                    </Typography>
                  )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {deciding !== null && (
        <DecisionDialog
          stepId={deciding.step.id}
          stepName={deciding.step.name}
          decision={deciding.decision}
          invalidate={[workflowHistoryKey(recordType, recordId)]}
          onClose={() => {
            setDeciding(null);
          }}
          {...(onDecided !== undefined ? { onDecided } : {})}
        />
      )}
    </Stack>
  );
}

export type Decision = 'approve' | 'reject' | 'return';

const DECISION_TEXT: Readonly<Record<Decision, { title: string; verb: string; message: string }>> =
  {
    approve: {
      title: 'Approve',
      verb: 'Approve',
      message: 'Your approval is recorded with your name and the time, and the request moves on.',
    },
    reject: {
      title: 'Reject',
      verb: 'Reject',
      message:
        'The request is rejected and its submitter is told. They may correct and resubmit it.',
    },
    return: {
      title: 'Return for changes',
      verb: 'Return',
      message: 'The request goes back to its submitter for changes and resubmission.',
    },
  };

/** Confirms one decision on one step, with an optional comment (FRD-CORE-015). */
export function DecisionDialog({
  stepId,
  stepName,
  decision,
  invalidate,
  onClose,
  onDecided,
}: {
  stepId: string;
  stepName: string;
  decision: Decision;
  invalidate: readonly (readonly unknown[])[];
  onClose: () => void;
  onDecided?: () => void;
}) {
  const session = useSession();
  const queryClient = useQueryClient();
  const [comment, setComment] = useState('');
  const text = DECISION_TEXT[decision];
  const decide = useMutation({
    mutationFn: () =>
      postJson(
        `/api/v1/approval-steps/${stepId}/decision`,
        { decision, ...(comment.trim() !== '' ? { comment: comment.trim() } : {}) },
        WorkflowInstanceSchema,
        session.data?.csrfToken ?? '',
      ),
    onSuccess: async () => {
      for (const key of invalidate) await queryClient.invalidateQueries({ queryKey: [...key] });
      await queryClient.invalidateQueries({ queryKey: ['approvals'] });
      onDecided?.();
      onClose();
    },
  });
  return (
    <ConfirmDialog
      open
      title={`${text.title}: ${stepName}`}
      message={text.message}
      confirmLabel={text.verb}
      tone={decision === 'approve' ? 'primary' : decision === 'reject' ? 'error' : 'warning'}
      busy={decide.isPending}
      error={decide.isError ? decide.error.message : null}
      onCancel={onClose}
      onConfirm={() => {
        decide.mutate();
      }}
    >
      <TextField
        label="Comment"
        multiline
        minRows={2}
        fullWidth
        sx={{ mt: 2 }}
        value={comment}
        onChange={(e) => {
          setComment(e.target.value);
        }}
        helperText="Optional. Recorded with your decision."
      />
      {decision !== 'approve' && (
        <Alert severity="info" variant="outlined" sx={{ mt: 2 }}>
          A comment helps the submitter act on your decision.
        </Alert>
      )}
    </ConfirmDialog>
  );
}
